import { motion } from "framer-motion";
import { FileText, CalendarClock, HeartHandshake, Video, BarChart3, Users, MessageSquare, Phone, CircleDollarSign } from "lucide-react";

const modules = [
  { icon: FileText, name: "EHR + AI Scribe", desc: "AI-generated clinical notes, labs and complete charting - documentation done before the patient leaves.", span: "md:col-span-2 md:row-span-2", metric: "SOAP to orders", featured: true },
  { icon: CalendarClock, name: "PMS", desc: "Practice management, smart scheduling, patient flow, tasks and waitlists.", span: "md:col-span-1", metric: "" },
  { icon: HeartHandshake, name: "Patient Engagement", desc: "Digital intake, reminders, messaging and portal.", span: "md:col-span-1", metric: "" },
  { icon: CircleDollarSign, name: "Revenue Cycle Management", desc: "Eligibility, authorization, claims, payments, denials and AR follow-up.", span: "md:col-span-2", metric: "" },
  { icon: Video, name: "Telehealth", desc: "HIPAA-compliant video visits, built in.", span: "md:col-span-1", metric: "" },
  { icon: BarChart3, name: "Analytics", desc: "Live operational, clinical and revenue dashboards.", span: "md:col-span-1", metric: "" },
  { icon: Users, name: "HRMS", desc: "Staff, payroll and credentialing for your team.", span: "md:col-span-1", metric: "" },
  { icon: MessageSquare, name: "Secure Chat", desc: "Real-time team and patient messaging.", span: "md:col-span-1", metric: "" },
  { icon: Phone, name: "Calls", desc: "Integrated VoIP and patient calling.", span: "md:col-span-1", metric: "" },
];

export const Modules = () => {
  return (
    <section id="modules" data-testid="modules" className="relative z-10 bg-white py-16 md:py-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-5">/ The Platform</p>
            <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[1.02]">
              Everything to run a<br /><span className="font-extrabold text-[#2880B8]">modern practice.</span>
            </h2>
          </div>
          <p className="max-w-sm text-[#51667A] leading-relaxed text-lg">
            One connected Astra platform for EHR + AI Scribe, PMS, RCM, HRMS, patient engagement, telehealth, secure communication and analytics.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[minmax(180px,auto)]">
          {modules.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                data-testid={`module-${m.name.toLowerCase().replace(/[ /+]+/g, "-")}`}
                className={`group relative rounded-2xl border p-6 md:p-8 flex flex-col justify-between transition-all duration-500 ${m.span} ${
                  m.featured
                    ? "bg-[#0C2A40] border-[#0C2A40] text-white"
                    : "bg-white border-[#D8E6F1] hover:border-[#2880B8] hover:shadow-[0_24px_50px_-24px_rgba(40,128,184,0.4)]"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-500 ${m.featured ? "bg-white/10" : "bg-[#E7F1F8] group-hover:bg-[#2880B8]"}`}>
                    <Icon size={m.featured ? 26 : 22} className={`${m.featured ? "text-[#9FD0EC]" : "text-[#2880B8] group-hover:text-white"} transition-colors duration-500`} strokeWidth={1.5} />
                  </div>
                  {m.metric && (
                    <span className={`font-mono text-[10px] tracking-[0.12em] uppercase ${m.featured ? "text-[#9FD0EC]" : "text-[#2880B8]"}`}>{m.metric}</span>
                  )}
                </div>
                <div>
                  <h3 className={`font-display font-semibold tracking-tight ${m.featured ? "text-white text-3xl md:text-4xl" : "text-[#0C2A40] text-xl"}`}>
                    {m.name}
                  </h3>
                  <p className={`mt-3 leading-relaxed ${m.featured ? "text-[#A9C4DD] text-base max-w-md" : "text-[#51667A] text-sm"}`}>
                    {m.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
